import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, take } from 'rxjs';
import { userRoles } from '../../../environments/environment';
import { ButtonComponent } from '../../shared/components/custom-button/button.component';
import { TableComponent } from '../../shared/components/custom-table/table.component';
import { AddSchoolComponent } from '../../shared/components/dialog/add-school/add-school.component';
import { AddStudentComponent } from '../../shared/components/dialog/add-student/add-student.component';
import { AddTeacherComponent } from '../../shared/components/dialog/add-teacher/add-teacher.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { ContactDialogComponent } from '../../shared/components/dialog/contact-details/contact-details-dialog.component';
import { ExportDropdownComponent } from '../../shared/components/export-dropdown/export-dropdown';
import { CustomToastComponent } from '../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../shared/services/http.service';
import { UrlParserService } from '../../shared/services/url-parser.service';

interface TeacherDialogData {
  initialData?: APIResponse;
  EditMode: boolean;
  ViewMode: boolean;
  schoolID: string;
}

interface APIResponse {
  schoolId: string;
  schoolName: string;
  address: string;
  suburb: string;
  postcode: string;
  state: string;
  regionName: string;
  regionId: string;
  schoolTypeId: string;
  teacherId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  yearLevel: number;
  dateOfBirth: string;
  status: string;
  name: string;
  phone: string;
  position: string;
  contactId: string;
  schoolTypeName: string;
  parentGuardianEmail: string;
  parentGuardianFullName: string;
  ethnicity: string;
  gender: string;
  personalEmail: string;
  schoolEmail: string;
  dateAdded: string;
  contacts: any;
  isMouSchool: boolean;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: any;
}

interface listAPIResponse {
  status: boolean;
  message: string;
  data: {
    totalRecord: number;
    items: any[];
  };
}

@Component({
  selector: 'app-schools',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ButtonComponent,
    MatDialogModule,
    ReactiveFormsModule,
    FormsModule,
    ExportDropdownComponent,
  ],
  templateUrl: './schools.component.html',
  styleUrls: ['./schools.component.scss'],
})
export class SchoolsComponent implements OnInit {
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild('actionButtonsTeacher') actionButtonsTeacher!: TemplateRef<any>;
  @ViewChild('actionButtonsStudent') actionButtonsStudent!: TemplateRef<any>;
  @ViewChild('actionButtonsContact') actionButtonsContact!: TemplateRef<any>;
  @ViewChild('addSchoolContent') addSchoolContent!: TemplateRef<any>;
  @ViewChild('schoolDetailsDialog') schoolDetailsDialog!: TemplateRef<any>;
  @ViewChild(TableComponent) tableComponent!: TableComponent;

  selectedSegment: 'teachers' | 'students' | 'contacts' = 'teachers';
  form!: FormGroup;
  viewMode = false;
  selectedSchool: any = {};
  selectedSchoolID: any = null;
  editingSchool: any = null;
  columns: string[] = [
    'school name',
    'Partnership / Non-Partnership School/s',
    'school type',
    'location/address',
    'date added',
    'status',
  ];
  exportColumns: string[] = [
    'School name',
    'Partnership / Non-Partnership School/s',
    'School type',
    'Location/Address',
    'Partnership School/s contact',
    'Date added',
    'Status',
  ];
  teachercolumns: string[] = ['teacher name', 'email', 'phone/mobile'];
  studentcolumns: string[] = ['first name', 'last name', 'email', 'year level'];
  contactcolumns: string[] = ['name', 'email', 'phone number', 'position'];
  loading = false;
  data: any[] = [];
  fullData: any[] = [];
  viewData: any[] = [];
  teacherData: any[] = [];
  studentData: any[] = [];
  contactData: any[] = [];
  search: string = '';
  searchControl = new FormControl('');
  isDeleteLoading = false;
  isSearchActive = false;
  private currentRequestId = 0;
  private isPageChangeLoading = false;

  userRoles = {
    addNew: false,
    export: false,
    listItem: {
      edit: false,
      view: false,
      delete: false,
      statusChange: false,
    },
  };
  userViewRoles = {
    edit: false,
    addItem: false,
    listItem: {
      edit: false,
      view: false,
      delete: false,
    },
  };
  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | '' = '';
  private schoolDetailDialogRef: any = null;

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private location: Location,
    private urlParser: UrlParserService,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
    const encodedId = params.get('id');
    const id = encodedId ? this.urlParser.decodeId(encodedId) : null;
    this.viewMode = !!id;
    if (this.viewMode && id) {
      this.selectedSchoolID = id || null;
    }
  });

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const page = params['page'] ? parseInt(params['page']) : 1;
      this.currentPage = page;

      if (!this.selectedSchoolID) {
        this.fetchSchools(this.currentPage);
      }
    });

    const user = localStorage.getItem('userRole');
    if (!this.selectedSchoolID) {
      if (user && user in userRoles) {
        this.userRoles = userRoles[user as keyof typeof userRoles]?.school;
      }
    } else if (this.selectedSchoolID) {
      if (user && user in userRoles) {
        this.userViewRoles =
          userRoles[user as keyof typeof userRoles]?.schoolView;
      }
      this.fetchViewSchools(this.selectedSchoolID);
      this.fetchViewTable(this.selectedSchoolID, this.selectedSegment);
      this.fetchViewContact(this.selectedSchoolID);
    }

    this.searchControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchValue) => {
        this.search = searchValue || '';
        this.currentPage = 1;
        this.updateUrlWithPage(1);
        this.fetchSchools(this.currentPage);
      });
  }

  fetchSchools(page: number = 1): void {
    this.loading = true;
    const requestId = ++this.currentRequestId;

    let url: string;
    if (this.search && this.search.trim() !== '') {
      url = `schools/get/search?search=${encodeURIComponent(this.search)}&page=${page}`;
      if (this.sortColumn && this.sortDirection) {
        const sortColumnName = this.getSortColumnName(this.sortColumn);
        url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
      }
    } else {
      url = `schools?page=${page}`;
      if (this.sortColumn && this.sortDirection) {
        const sortColumnName = this.getSortColumnName(this.sortColumn);
        url += `&orderby=${this.sortDirection}&orderbyname=${sortColumnName}`;
      }
    }

    this.httpService.get<listAPIResponse>(url).subscribe({
      next: (response) => {
        if (requestId !== this.currentRequestId) return;

        const items = response?.data?.items || [];
        this.totalRecords = response?.data?.totalRecord || 0;

        this.fullData = items.map((school: any) => ({
          id: school.schoolId ?? '',
          'school name': school.schoolName ?? '',
          address: school.address ?? '',
          suburb: school.suburb ?? '',
          postcode: school.postcode ?? '',
          state: school.state ?? '',
          region: school.regionName ?? '',
          regionId: school.regionId ?? '',
          schoolTypeId: school.schoolTypeId ?? '',
          isMouSchool: school.isMouSchool ?? false,
          status: school.status ?? 'Active',
          'school type': school.schoolTypeName ?? '',
          'Partnership / Non-Partnership School/s': school.isMouSchool
            ? 'Partnership School'
            : 'Non-Partnership School',
          'location/address': [
            school.address,
            school.suburb,
            school.state,
            school.postcode,
          ]
            .filter(Boolean)
            .join(', '),
          'date added': school.dateAdded
            ? new Date(school.dateAdded).toLocaleDateString('en-GB')
            : '',
          // The table renders this cell per contact, so hand it every contact rather than a
          // formatted string - a school can have more than one.
          'Partnership School/s contact': Array.isArray(school?.contacts)
            ? school.contacts
                .filter((contact: any) => contact?.name || contact?.email)
                .map((contact: any) => ({
                  name: contact?.name ?? '',
                  email: contact?.email ?? '',
                }))
            : [],
          isStatusClickable: true,
        }));

        this.data = [...this.fullData];
        this.loading = false;
        this.isPageChangeLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (requestId !== this.currentRequestId) return;

        console.error('Failed to load schools:', err);
        this.fullData = [];
        this.data = [];
        this.loading = false;
        this.isPageChangeLoading = false;
        this.showErrorToast(err?.message);
      },
    });
  }

  onExportComplete(type: 'csv' | 'pdf' | 'excel'): void {
    // You can show a success message or perform any other actions
    this.showSuccessToast(
      `Export completed successfully as ${type.toUpperCase()}`,
    );
  }

  private getSortColumnName(column: string): string {
    const columnMapping: { [key: string]: string } = {
      'school name': 'SchoolName',
      'mou / non-mou': 'IsMouSchool',
      'partnership / non-partnership school/s': 'IsMouSchool',
      'school type': 'SchoolTypeName',
      'location/address': 'Address',
      address: 'Address',
      'mou contact': 'ContactName',
      'partnership school/s contact': 'ContactName',
      status: 'Status',
      'date added': 'CreatedAt',
      region: 'RegionName',
      'first name': 'FirstName',
      'last name': 'LastName',
      'teacher name': 'TeacherName',
      email: 'Email',
      'phone/mobile': 'PhoneNumber',
      'phone number': 'PhoneNumber',
      'year level': 'YearLevel',
      name: 'Name',
      position: 'Position',
    };

    return columnMapping[column.toLowerCase()] || column;
  }

  transformApiData(items: any[]): any[] {
    return items.map((user) => {
      const contact =
        Array.isArray(user?.contacts) && user.contacts.length > 0
          ? user.contacts[0]
          : null;
      return {
        'School name': user?.schoolName ?? '',
        'Partnership / Non-Partnership School/s': user?.isMouSchool
          ? 'Partnership School'
          : 'Non-Partnership School',
        'School type': user?.schoolTypeName ?? '',
        'Location/Address': [
          user?.address,
          user?.suburb,
          user?.state,
          user?.postcode,
        ]
          .filter(Boolean)
          .join(', '),
        'Partnership School/s contact': {
          name: contact?.name ?? ' ',
          email: contact?.email ?? ' ',
        },
        Status: user?.status ?? 'Active',
        'Date added': user?.dateAdded
          ? new Date(user.dateAdded).toLocaleDateString('en-GB')
          : '',
      };
    });
  }

  updateSearch(searchValue: string): void {
    this.search = searchValue;
    this.searchControl.setValue(searchValue);
    this.cdr.detectChanges();
  }

  onSort(event: {
    direction: 'asc' | 'desc' | '';
    column: string | null;
  }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.currentPage = 1;
    this.updateUrlWithPage(this.currentPage);
    this.fetchSchools(this.currentPage);
  }

  private resetPagination(): void {
    this.data = [...this.data];
    if (this.tableComponent) {
      this.tableComponent.currentPage = 1;
      this.tableComponent.cdr?.detectChanges();
    }
  }

  fetchViewSchools(id: any): void {
    this.loading = true;
    this.httpService.get<ApiDataResponse>(`schools/${id}/details`).subscribe({
      next: (response) => {
        this.selectedSchool = response?.data;
      },
      error: (err) => {
        console.error('Failed to load schools:', err);
        this.selectedSchool = {};
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  fetchViewTable(id: any, type: string): void {
    this.loading = true;
    this.httpService.get<ApiDataResponse>(`schools/${id}/${type}`).subscribe({
      next: (response) => {
        if (Array.isArray(response?.data)) {
          if (type === 'teachers') {
            this.teacherData = response?.data.map((school) => ({
              id: school?.teacherId ?? '',
              'teacher name': [school?.firstName, school?.lastName]
                .filter(Boolean)
                .join(' '),
              email: school?.email ?? '',
              'phone/mobile': school?.phoneNumber ?? '',
              firstName: school?.firstName ?? '',
              lastName: school?.lastName ?? '',
            }));
          } else if (type === 'students') {
            this.studentData = response?.data.map((school) => ({
              id: school?.studentId ?? '',
              'first name': school?.firstName ?? '',
              'last name': school?.lastName ?? '',
              'year level': school?.yearLevel ?? '',
              dateOfBirth: school?.dateOfBirth ?? '',
              email: school?.schoolEmail ?? '',
              personalEmail: school?.personalEmail ?? '',
              gender: school?.gender ?? '',
              ethnicity: school?.ethnicity ?? ' ',
              parentGuardianFullName: school?.parentGuardianFullName ?? '',
              parentGuardianEmail: school?.parentGuardianEmail ?? '',
              firstName: school?.firstName ?? '',
              lastName: school?.lastName ?? '',
              ethnicityId: school?.ethnicityId ?? '',
              yearLevelId: school?.yearLevelId ?? '',
              past: school?.past,
              comp: school?.comp,
            }));
          }
        } else {
          if (type === 'teachers') {
            this.teacherData = [];
          } else if (type === 'students') {
            this.studentData = [];
          }
        }
      },
      error: (err) => {
        console.error('Failed to load schools:', err);
        if (type === 'teachers') {
          this.teacherData = [];
        } else if (type === 'students') {
          this.studentData = [];
        }
        this.loading = false;
        this.showErrorToast(
          err?.message || 'Something went wrong. Please try again later.',
        );
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  fetchViewContact(id: any): void {
    this.loading = true;
    this.httpService.get<ApiDataResponse>(`schools/${id}/contacts`).subscribe({
      next: (response) => {
        if (Array.isArray(response?.data)) {
          this.contactData = response?.data.map((school) => ({
            id: school?.contactId ?? '-',
            name: school?.name ?? '-',
            email: school?.email ?? '-',
            'phone number': school?.phone ?? '-',
            position: school?.position ?? '-',
          }));
        } else {
          this.contactData = [];
        }
      },
      error: (err) => {
        console.error('Failed to load schools:', err);
        this.contactData = [];
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  initForm() {
    this.form = this.fb.group({
      schoolName: ['', Validators.required],
      address: ['', Validators.required],
      suburb: ['', Validators.required],
      postcode: ['', Validators.required],
      state: ['', Validators.required],
      isMouSchool: [false],
    });
  }

  openAddSchoolDialog(
    schoolToEdit: any = null,
    isEdit: boolean = false,
    width: string = '50.5rem',
  ): void {
    this.editingSchool = schoolToEdit || null;
    const dialogRef = this.dialog.open(AddSchoolComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: schoolToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchSchools(this.currentPage);
        if (this.selectedSchoolID) {
          this.fetchViewSchools(this.selectedSchoolID);
          this.fetchViewTable(this.selectedSchoolID, this.selectedSegment);
          this.fetchViewContact(this.selectedSchoolID);
        }
      }
      this.editingSchool = null;
    });
  }

  onStatusUpdate(event: { row: any; id: any }): void {
    if (!this.userRoles.listItem.statusChange) return;
    const { row } = event;
    const currentStatus = row.status;
    const newStatus = currentStatus === 'Active' ? false : true;

    const dialogData: AlertDialogData = {
      title: 'Are you sure!',
      message: `Are you sure you want to ${currentStatus === 'Active' ? 'Deactivate' : 'Activate'} ?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.httpService
            .put<ApiDataResponse>(`schools/${row?.id}/toggle-status`, {
              status: newStatus,
            })
            .subscribe({
              next: (response) => {
                if (response.status) {
                  this.updateSchoolInArrays(row.id, {
                    status: newStatus ? 'Active' : 'Inactive',
                  });
                  this.showSuccessToast(response.message);
                } else {
                  this.showErrorToast(response.message);
                }
              },
              error: (err) => this.showErrorToast(err?.message),
            });
        }
      });
  }

  // onView(school: any): void {
  //   this.selectedSegment = 'teachers';
  //   this.selectedSchoolID = school.id;
  //   this.selectedSchool = {};
  //   this.teacherData = [];
  //   this.contactData = [];

  //   this.fetchViewSchools(this.selectedSchoolID);
  //   this.fetchViewTable(this.selectedSchoolID, 'teachers');
  //   this.fetchViewContact(this.selectedSchoolID);

  //   this.schoolDetailDialogRef = this.dialog.open(this.schoolDetailsDialog, {
  //     width: '72rem',
  //     maxWidth: '95vw',
  //     autoFocus: false,
  //     panelClass: 'custom-dialog-container',
  //   });

  //   this.schoolDetailDialogRef.afterClosed().subscribe(() => {
  //     if (!this.viewMode) {
  //       this.selectedSchoolID = null;
  //       this.selectedSchool = {};
  //       this.teacherData = [];
  //       this.contactData = [];
  //       this.selectedSegment = 'teachers';
  //     }
  //     this.schoolDetailDialogRef = null;
  //   });
  // }

  onView(school: any): void {
    const encodedId = this.urlParser.encodeId(school.id.toString());
    this.router.navigate(['/schools/view/' + encodedId]);
  }

  onTeacherView(school: any): void {
    const encodedId = this.urlParser.encodeId(school.id.toString());
    this.router.navigate(['/teachers/view/' + encodedId]);
  }

  onStudentView(school: any): void {
    const encodedId = this.urlParser.encodeId(school.id.toString());
    this.router.navigate(['/students/view/' + encodedId]);
  }

  goBack() {
    this.location.back();
  }

  onEdit(school: any): void {
    this.openAddSchoolDialog(school, true);
  }

  onTeacherEdit(school: any): void {
    this.openAddTeacherDialog(school, true);
  }

  onStudentEdit(school: any): void {
    this.openAddStudentDialog(school, true);
  }

  onContactEdit(school: any): void {
    this.openAddContactDialog(school, true);
  }

  onContactView(school: any): void {
    this.openAddContactDialog(school, false, '', true);
  }

  onDelete(school: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure want to delete ?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.beforeClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.httpService
        .delete<ApiDataResponse>(`schools/${school.id}`)
        .subscribe({
          next: (response) => {
            if (response.status) {
              this.removeSchoolFromArrays(school.id);
              this.adjustPaginationAfterDelete();
              this.showSuccessToast(response.message);
            } else {
              this.showErrorToast(response.message);
            }
          },
          error: (err) => {
            console.error('Failed to delete school:', err);
            this.showErrorToast(err?.message);
          },
        });
    });
  }

  getExportApiUrl(): string {
    if (this.search && this.search.trim() !== '') {
      return 'schools/get/search/export';
    } else {
      return 'schools/exports';
    }
  }

  getCurrentExportParams(): any {
    const params: any = {
      orderby: this.sortDirection || 'desc',
      orderbyname: this.sortColumn
        ? this.getSortColumnName(this.sortColumn)
        : 'CreatedAt',
    };
    if (this.search && this.search.trim() !== '') {
      params.search = this.search;
    }
    return params;
  }

  onTeacherDelete(teacher: any): void {
    const fullName =
      `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() ||
      'Teacher';
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure you want to delete ${fullName}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.httpService
          .delete<ApiDataResponse>(`teachers/${teacher.id}`)
          .subscribe({
            next: (response) => {
              if (response.status) {
                this.removeTeacherFromArray(teacher.id);
                this.adjustPaginationAfterDelete();
                this.showSuccessToast(response.message);
              } else {
                this.showErrorToast(response.message);
              }
            },
            error: (err) => {
              console.error('Failed to delete teacher:', err);
              this.showErrorToast(err?.message);
            },
          });
      }
    });
  }

  onDeleteConatct(contact: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure you want to delete ${contact.name}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    const dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '25.438rem',
      data: dialogData,
    });

    dialogRef.beforeClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.httpService
        .delete<ApiDataResponse>(
          `schools/${this.selectedSchoolID}/contacts/${contact.id}`,
        )
        .subscribe({
          next: (response) => {
            if (response.status) {
              this.removeContactFromArray(contact.id);
              this.adjustPaginationAfterDelete();
              this.showSuccessToast(response.message);
            } else {
              this.showErrorToast(response.message);
            }
          },
          error: (err) => {
            console.error('Failed to delete contact:', err);
            this.showErrorToast(err?.message);
          },
        });
    });
  }

  openAddTeacherDialog(
    teacherToEdit: APIResponse | undefined = undefined,
    isEdit: boolean = false,
    width: string = '50.5rem',
  ) {
    this.form.reset();

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: {
        initialData: teacherToEdit,
        EditMode: isEdit,
        schoolID: this.selectedSchoolID,
        ViewMode: false,
      },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    const dialogRef = this.dialog.open(AddTeacherComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.fetchViewTable(this.selectedSchoolID, this.selectedSegment);
      }
    });
  }

  openAddStudentDialog(
    teacherToEdit: APIResponse | undefined = undefined,
    isEdit = false,
    width = '50.5rem',
  ): void {
    this.form.reset();

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: {
        initialData: teacherToEdit,
        EditMode: isEdit,
        schoolID: this.selectedSchoolID,
        ViewMode: false,
      },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    this.dialog
      .open(AddStudentComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.fetchViewTable(this.selectedSchoolID, this.selectedSegment);
        }
      });
  }

  openAddContactDialog(
    teacherToEdit: APIResponse | undefined = undefined,
    isEdit = false,
    width = '50.5rem',
    view = false,
  ): void {
    this.form.reset();

    const dialogConfig: MatDialogConfig<TeacherDialogData> = {
      width,
      data: {
        initialData: teacherToEdit,
        EditMode: isEdit,
        schoolID: this.selectedSchoolID,
        ViewMode: view,
      },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    };

    this.dialog
      .open(ContactDialogComponent, dialogConfig)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.fetchViewContact(this.selectedSchoolID);
        }
      });
  }

  onStudentDelete(student: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure you want to delete ${student.firstName} ${student.lastName}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.httpService
            .delete<ApiDataResponse>(`students/${student.id}`)
            .subscribe({
              next: (response) => {
                if (response.status) {
                  this.removeStudentFromArray(student.id);
                  this.adjustPaginationAfterDelete();
                  this.showSuccessToast(response.message);
                } else {
                  this.showErrorToast(response.message);
                }
              },
              error: (err) => this.showErrorToast(err?.message),
            });
        }
      });
  }

  switchSegment(segment: 'teachers' | 'students' | 'contacts') {
    this.selectedSegment = segment;
    this.fetchViewTable(this.selectedSchoolID, this.selectedSegment);
  }

  private updateSchoolInArrays(schoolId: string, updates: any): void {
    const fullDataIndex = this.fullData.findIndex(
      (school) => school.id === schoolId,
    );
    if (fullDataIndex !== -1) {
      this.fullData[fullDataIndex] = {
        ...this.fullData[fullDataIndex],
        ...updates,
      };
    }
    const dataIndex = this.data.findIndex((school) => school.id === schoolId);
    if (dataIndex !== -1) {
      this.data[dataIndex] = { ...this.data[dataIndex], ...updates };
      this.data = [...this.data];
    }
  }

  private removeSchoolFromArrays(schoolId: string): void {
    this.fullData = this.fullData.filter((school) => school.id !== schoolId);
    this.data = this.data.filter((school) => school.id !== schoolId);
  }

  private removeTeacherFromArray(teacherId: string): void {
    this.teacherData = this.teacherData.filter(
      (teacher) => teacher.id !== teacherId,
    );
  }

  private removeStudentFromArray(studentId: string): void {
    this.studentData = this.studentData.filter(
      (student) => student.id !== studentId,
    );
  }

  private removeContactFromArray(contactId: string): void {
    this.contactData = this.contactData.filter(
      (contact) => contact.id !== contactId,
    );
  }

  private adjustPaginationAfterDelete(): void {
    const totalPages = Math.ceil(this.totalRecords / this.pageSize);
    if (this.currentPage > totalPages && totalPages > 0) {
      this.currentPage = totalPages;
      this.updateUrlWithPage(this.currentPage);
      this.fetchSchools(this.currentPage);
    } else {
      this.fetchSchools(this.currentPage);
    }
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || this.isPageChangeLoading) return;

    this.isPageChangeLoading = true;
    this.currentPage = page;
    this.updateUrlWithPage(page);
    this.fetchSchools(page);
  }

  private updateUrlWithPage(page: number): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: page,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  formatSchoolAddress(school: any): string {
    return [school?.address, school?.suburb, school?.state, school?.postcode]
      .filter(Boolean)
      .join(', ');
  }

  formatContactSummary(contact: any): string {
    return [
      contact?.name,
      contact?.email,
      contact?.['phone number'],
      contact?.position,
    ]
      .filter(Boolean)
      .join(' | ');
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
